<template>
  <div class="pg-write">
    <!-- Header -->
    <ACard>
      <div class="pg-write__header">
        <div class="pg-write__header-text">
          <div class="pg-write__header-title">
            <i class="material-icons-round">create</i>
            Writing Practice
          </div>
          <p class="pg-write__header-sub">학습자의 작문 능력을 평가하는 루브릭과 체크리스트를 설정하세요</p>
        </div>
        <ABtn kind="ghost" icon="restore" @click.prevent="resetToDefaultTemplate">
          기본 템플릿 로드
        </ABtn>
      </div>
    </ACard>

    <!-- Essay Topic & Sample Script Section -->
    <ACard title="작문 주제 및 예시">
      <template #title>
        <span class="pg-write__section-title">
          <i class="material-icons-round">description</i>
          작문 주제 및 예시
        </span>
      </template>

      <div class="pg-write__fields">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round pg-write__label-ico">topic</i>
            작문 주제 (Essay Topic)
          </label>
          <textarea
            v-model="sectionData.content.essayTopic"
            class="p-input p-input--area"
            rows="3"
            placeholder="작문 주제를 입력하세요 (예: Describe your favorite place to visit and explain why you enjoy it)"
          />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round pg-write__label-ico">article</i>
            예시 작문 (Sample Script)
          </label>
          <textarea
            v-model="sectionData.content.sampleScript"
            class="p-input p-input--area"
            rows="6"
            placeholder="예시 작문을 입력하세요 (예: My favorite place to visit is the local library. It is a quiet and peaceful environment where I can focus on my studies. The library has a vast collection of books on various topics, which allows me to explore new subjects and expand my knowledge. Additionally, the comfortable seating areas and natural lighting create the perfect atmosphere for reading and learning.)"
          />
        </div>
      </div>
    </ACard>

    <!-- Checklist Section -->
    <ACard v-if="sectionData.content?.evalInfo">
      <template #title>
        <span class="pg-write__section-title">
          <i class="material-icons-round">checklist</i>
          체크리스트 (Checklist)
        </span>
      </template>
      <template #action>
        <ABtn kind="yellow" size="sm" icon="add" @click.prevent="addChecklistItem">
          항목 추가
        </ABtn>
      </template>

      <div class="pg-write__list">
        <div
          v-for="(item, index) in sectionData.content.evalInfo.checklist"
          :key="index"
          class="pg-write__item"
        >
          <div class="pg-write__item-head">
            <span class="pg-write__index">Item {{ index + 1 }}</span>
            <div class="pg-write__item-actions">
              <button
                type="button"
                class="pg-write__icon-btn pg-write__icon-btn--danger"
                title="삭제"
                @click.prevent="removeChecklistItem(index)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>
          <div class="pg-write__item-body">
            <div class="p-form-grid p-form-grid--4">
              <div class="p-field pg-write__col-1">
                <label class="p-field__label">항목 코드 (Checklist ID)</label>
                <input
                  v-model="item.checklistId"
                  type="text"
                  class="p-input"
                  placeholder="WR010601"
                />
              </div>
              <div class="p-field pg-write__col-3">
                <label class="p-field__label">체크리스트 내용 (Checklist Name)</label>
                <textarea
                  v-model="item.checklistName"
                  class="p-input p-input--area"
                  rows="2"
                  placeholder="Clear topic sentence that introduces the main idea"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <AEmpty
          v-if="!sectionData.content.evalInfo.checklist || sectionData.content.evalInfo.checklist.length === 0"
          size="sm"
          title="체크리스트 항목이 없습니다"
        >
          <template #action>
            <ABtn kind="yellow" icon="add" @click.prevent="addChecklistItem">첫 항목 추가</ABtn>
          </template>
        </AEmpty>
      </div>
    </ACard>

    <!-- Rubric Section -->
    <ACard v-if="sectionData.content?.evalInfo">
      <template #title>
        <span class="pg-write__section-title">
          <i class="material-icons-round">assessment</i>
          평가 기준 (Rubric)
        </span>
      </template>
      <template #action>
        <ABtn kind="yellow" size="sm" icon="add" @click.prevent="addRubricItem">
          평가 기준 추가
        </ABtn>
      </template>

      <div class="pg-write__list">
        <div
          v-for="(rubricItem, rubricIndex) in sectionData.content.evalInfo.rubric"
          :key="rubricIndex"
          class="pg-write__item"
        >
          <div class="pg-write__item-head">
            <span class="pg-write__index">Rubric {{ rubricIndex + 1 }}</span>
            <div class="pg-write__item-actions">
              <button
                v-if="sectionData.content.evalInfo.rubric.length > 1"
                type="button"
                class="pg-write__icon-btn pg-write__icon-btn--danger"
                title="평가 기준 삭제"
                @click.prevent="removeRubricItem(rubricIndex)"
              >
                <i class="material-icons-round">delete</i>
              </button>
            </div>
          </div>

          <div class="pg-write__item-body">
            <!-- Criteria -->
            <div class="p-field">
              <label class="p-field__label">평가 항목 (Criteria)</label>
              <textarea
                v-model="rubricItem.criteria"
                class="p-input p-input--area"
                rows="2"
                placeholder="평가 기준을 입력하세요 (예: Content and Organization)"
              />
            </div>

            <!-- Ratings -->
            <div class="p-field">
              <label class="p-field__label">점수 등급 (Ratings)</label>
              <div class="pg-write__ratings">
                <div
                  v-for="(rating, ratingIndex) in rubricItem.ratings"
                  :key="ratingIndex"
                  class="pg-write__rating"
                >
                  <span class="pg-write__rating-point">{{ rating.point }}점</span>
                  <input
                    v-model="rating.name"
                    type="text"
                    class="p-input"
                    placeholder="점수 설명 (예: Clear organization with excellent content)"
                  />
                  <button
                    v-if="rubricItem.ratings.length > 1"
                    type="button"
                    class="pg-write__icon-btn pg-write__icon-btn--danger"
                    title="등급 삭제"
                    @click.prevent="removeRating(rubricIndex, ratingIndex)"
                  >
                    <i class="material-icons-round">delete</i>
                  </button>
                </div>
              </div>
              <div class="pg-write__rating-add">
                <ABtn kind="ghost" size="sm" icon="add" @click.prevent="addRating(rubricIndex)">
                  등급 추가
                </ABtn>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <AEmpty
          v-if="sectionData.content.evalInfo.rubric.length === 0"
          size="sm"
          title="평가 기준이 없습니다"
        >
          <template #action>
            <ABtn kind="yellow" icon="add" @click.prevent="addRubricItem">첫 평가 기준 추가</ABtn>
          </template>
        </AEmpty>
      </div>
    </ACard>

    <!-- Rubric PDF file upload -->
    <ACard>
      <template #title>
        <span class="pg-write__section-title">
          <i class="material-icons-round">attach_file</i>
          평가 기준 PDF 파일 업로드
        </span>
      </template>

      <div class="pg-write__pdf-grid">
        <div class="p-field">
          <label class="p-field__label">평가 기준 PDF 파일</label>
          <div class="pg-write__pdf">
            <input
              id="rubric-pdf-input"
              type="file"
              class="pg-write__pdf-input"
              accept="application/pdf"
              @change="onPdfChange($event, 'rubric')"
            />
            <label
              for="rubric-pdf-input"
              class="pg-write__pdf-area"
              v-if="!sectionData.content?.evalInfo?.rubricPdfUrl && !pdfUploadingState.rubric"
            >
              <div class="pg-write__pdf-area-content">
                <i class="material-icons-round pg-write__pdf-ico">picture_as_pdf</i>
                <div class="pg-write__pdf-text">
                  <strong>PDF 선택</strong>
                  <small>클릭하여 파일을 선택하세요</small>
                </div>
              </div>
            </label>
            <div class="pg-write__pdf-uploading" v-if="pdfUploadingState.rubric">
              <i class="material-icons-round pg-write__pdf-rotate">autorenew</i>
              <span>업로드 중... {{ pdfUploadProgress.rubric }}%</span>
            </div>
            <div
              class="pg-write__pdf-preview"
              v-if="sectionData.content?.evalInfo?.rubricPdfUrl && !pdfUploadingState.rubric"
            >
              <i class="material-icons-round pg-write__pdf-ico">picture_as_pdf</i>
              <div class="pg-write__pdf-meta">
                <div class="pg-write__pdf-name">
                  {{ getFileNameFromUrl(sectionData.content.evalInfo.rubricPdfUrl) }}
                </div>
                <div class="pg-write__pdf-actions">
                  <a :href="sectionData.content.evalInfo.rubricPdfUrl" target="_blank">파일 보기</a>
                  <ABtn kind="ghost" size="sm" @click.prevent="changePdfFile('rubric')">변경</ABtn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ACard>

  </div>
</template>

<script setup>
import { defineModel, onMounted, watch, reactive, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import defaultTemplate from '@/assets/writing-default.json';
import ABtn from '@/components/Pineapple/ABtn.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';

const sectionData = defineModel({ type: Object, required: true });
const route = useRoute();
const instance = getCurrentInstance();
const $axios = instance?.proxy?.$axios;

const initializeContent = () => {
  if (!sectionData.value) {
    sectionData.value = {};
  }

  if (!sectionData.value.content) {
    sectionData.value.content = {};
  }

  // Initialize essayTopic and sampleScript
  if (!sectionData.value.content.essayTopic) {
    sectionData.value.content.essayTopic = '';
  }

  if (!sectionData.value.content.sampleScript) {
    sectionData.value.content.sampleScript = '';
  }

  // Initialize evalInfo structure
  if (!sectionData.value.content.evalInfo) {
    sectionData.value.content.evalInfo = {
      rubric: [],
      checklist: []
    };
  }

  // Initialize rubric if not exists
  if (!sectionData.value.content.evalInfo.rubric) {
    sectionData.value.content.evalInfo.rubric = [];
  }

  // Initialize checklist if not exists
  if (!sectionData.value.content.evalInfo.checklist) {
    sectionData.value.content.evalInfo.checklist = [];
  }
};

// Normalize evalInfo structure from template or legacy data
const normalizeEvalInfo = () => {
  if (!sectionData.value?.content) return;
  if (!sectionData.value.content.evalInfo) {
    sectionData.value.content.evalInfo = { rubric: [], checklist: [] };
  }
  const evalInfo = sectionData.value.content.evalInfo;

  if (!Array.isArray(evalInfo.rubric)) {
    evalInfo.rubric = [];
  }

  // Support legacy key: checkList (object) -> checklist (array)
  if (!Array.isArray(evalInfo.checklist)) {
    if (Array.isArray(evalInfo.checkList)) {
      evalInfo.checklist = evalInfo.checkList;
    } else if (evalInfo.checkList && typeof evalInfo.checkList === 'object') {
      evalInfo.checklist = Object.values(evalInfo.checkList);
    } else if (!evalInfo.checklist) {
      evalInfo.checklist = [];
    }
    if (evalInfo.checkList !== undefined) delete evalInfo.checkList;
  }
};

// Function to load default template data
const loadDefaultTemplate = () => {
  if (defaultTemplate?.evalInfo) {
    // Deep copy the template data to avoid mutations
    sectionData.value.content.evalInfo = JSON.parse(JSON.stringify(defaultTemplate.evalInfo));
  } else {
    // Fallback to empty structure if template fails to load
    sectionData.value.content.evalInfo = {
      rubric: [
        {
          criteria: '',
          ratings: [
            { point: 5, name: '' },
            { point: 4, name: '' },
            { point: 3, name: '' },
            { point: 2, name: '' },
            { point: 1, name: '' },
            { point: 0, name: '' }
          ]
        }
      ],
      checklist: []
    };
  }
  // Ensure structure is normalized after loading
  normalizeEvalInfo();
};

// Function to reset to default template (useful for user action)
const resetToDefaultTemplate = () => {
  if (confirm('정말로 기본 템플릿으로 초기화하시겠습니까? 현재 내용은 모두 삭제됩니다.')) {
    loadDefaultTemplate();
  }
};

// Rubric methods
const addRubricItem = () => {
  if (!sectionData.value.content?.evalInfo) {
    initializeContent();
  }
  normalizeEvalInfo();
  sectionData.value.content.evalInfo.rubric.push({
    criteria: '',
    ratings: [
      { point: 5, name: '' },
      { point: 4, name: '' },
      { point: 3, name: '' },
      { point: 2, name: '' },
      { point: 1, name: '' },
      { point: 0, name: '' }
    ]
  });
};

const removeRubricItem = (index) => {
  sectionData.value.content.evalInfo.rubric.splice(index, 1);
};

const addRating = (rubricIndex) => {
  const ratings = sectionData.value.content.evalInfo.rubric[rubricIndex].ratings;
  const maxPoint = ratings.length > 0 ? Math.max(...ratings.map(r => r.point)) : -1;
  const newPoint = maxPoint + 1;

  sectionData.value.content.evalInfo.rubric[rubricIndex].ratings.push({
    point: newPoint,
    name: ''
  });

  // 점수 기준으로 내림차순 정렬
  sectionData.value.content.evalInfo.rubric[rubricIndex].ratings.sort((a, b) => b.point - a.point);
};

const removeRating = (rubricIndex, ratingIndex) => {
  sectionData.value.content.evalInfo.rubric[rubricIndex].ratings.splice(ratingIndex, 1);
};

// Checklist methods - No sampleExpression field for WritingPractice
const addChecklistItem = () => {
  if (!sectionData.value.content?.evalInfo) {
    initializeContent();
  }
  normalizeEvalInfo();
  // Generate a new unique ID
  const timestamp = Date.now();
  const newId = `WR${timestamp.toString().slice(-6)}`; // WR prefix for Writing

  sectionData.value.content.evalInfo.checklist.push({
    checklistId: newId,
    checklistName: ''
    // No sampleExpression field for WritingPractice
  });
};

const removeChecklistItem = (index) => {
  sectionData.value.content.evalInfo.checklist.splice(index, 1);
};

// PDF upload state
const pdfUploadingState = reactive({ rubric: false, checklist: false });
const pdfUploadProgress = reactive({ rubric: 0, checklist: 0 });

// PDF upload handler
const onPdfChange = async (event, type) => {
  const file = event.target.files?.[0];
  if (!file) return;

  if (file.type !== 'application/pdf') {
    alert('PDF 파일만 업로드 가능합니다.');
    event.target.value = '';
    return;
  }

  // 20MB 제한 (필요 시 조정)
  if (file.size > 20 * 1024 * 1024) {
    alert('PDF 파일 크기는 20MB 이하여야 합니다.');
    event.target.value = '';
    return;
  }

  const barcode = route.query.barcode;
  if (!barcode) {
    alert('barcode가 없습니다. 페이지 주소의 barcode를 확인해주세요.');
    event.target.value = '';
    return;
  }

  // uniqueKey가 없으면 신규 등록 상태이므로 업로드 제한
  const uniqueKey = sectionData.value?.uniqueKey;
  if (!uniqueKey) {
    alert('이 섹션은 아직 신규 등록 상태입니다. 섹션을 먼저 저장(등록)한 후 업데이트 단계에서 PDF를 업로드해 주세요.');
    event.target.value = '';
    return;
  }

  pdfUploadingState[type] = true;
  pdfUploadProgress[type] = 0;

  try {
    const formData = new FormData();
    // 파일명을 {uniqueKey}_rubric.pdf 또는 {uniqueKey}_checklist.pdf로 지정
    const desiredName = `${uniqueKey}_${type === 'rubric' ? 'rubric' : 'checklist'}.pdf`;
    const renamedFile = new File([file], desiredName, { type: file.type });
    formData.append('files', renamedFile, desiredName);
    formData.append('uploadPath', `textbookContents/${barcode}/assets/doc`);

    const res = await $axios({
      method: 'post',
      url: '/common/fileUpload',
      data: formData,
      loadingBar: true,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          pdfUploadProgress[type] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        }
      }
    });

    if (res?.data?.success && Array.isArray(res.data.uploaded_files) && res.data.uploaded_files.length > 0) {
      normalizeEvalInfo();
      const url = res.data.uploaded_files[0];
      if (type === 'rubric') {
        sectionData.value.content.evalInfo.rubricPdfUrl = url;
      } else {
        sectionData.value.content.evalInfo.checklistPdfUrl = url;
      }
    } else {
      alert('업로드된 파일 정보가 없습니다.');
    }
  } catch (error) {
    console.error('PDF 업로드 실패:', error);
    alert(`PDF 업로드에 실패했습니다: ${error?.message || error}`);
  } finally {
    pdfUploadingState[type] = false;
    pdfUploadProgress[type] = 0;
    event.target.value = '';
  }
};

// Trigger file input for change action
const changePdfFile = (type) => {
  const id = type === 'rubric' ? 'rubric-pdf-input' : 'checklist-pdf-input';
  const el = document.getElementById(id);
  if (el) el.click();
};

// Extract filename from URL for preview
const getFileNameFromUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  try {
    const decoded = decodeURIComponent(url);
    const parts = decoded.split('?')[0].split('/');
    return parts[parts.length - 1] || '';
  } catch (e) {
    const parts = url.split('?')[0].split('/');
    return parts[parts.length - 1] || '';
  }
};

// Initialize immediately
initializeContent();

// Watch for changes in sectionData
watch(() => sectionData.value, () => {
  if (!sectionData.value?.content?.evalInfo) {
    initializeContent();
  }
}, { immediate: true, deep: true });

onMounted(() => {
  initializeContent();
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-write {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  &__header-text {
    flex: 1;
    min-width: 0;
  }

  &__header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 800;
    color: $p-ink;
    letter-spacing: -0.015em;

    > i { font-size: 20px; color: $p-point-deep; }
  }

  &__header-sub {
    margin: 6px 0 0;
    font-size: 12px;
    color: $p-ink-soft;
  }

  &__section-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;

    > i {
      font-size: 16px;
      color: $p-point-deep;
    }
  }

  &__label-ico {
    font-size: 14px !important;
    color: $p-ink-mid;
    vertical-align: middle;
    margin-right: 4px;
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    padding: 4px 12px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &--danger {
      color: $p-err;

      &:hover:not(:disabled) {
        background: $p-err-soft;
        color: $p-err;
      }
    }
  }

  &__item-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__col-1 {
    grid-column: span 1;
  }

  &__col-3 {
    grid-column: span 3;

    @media (max-width: 768px) {
      grid-column: span 1;
    }
  }

  &__ratings {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__rating {
    display: grid;
    grid-template-columns: 60px 1fr auto;
    gap: 8px;
    align-items: center;
  }

  &__rating-point {
    padding: 4px 10px;
    border-radius: 999px;
    background: $p-panel-alt;
    color: $p-ink;
    border: 1px solid $p-line;
    font-family: $p-font-mono;
    font-size: 11px;
    font-weight: 800;
    text-align: center;
  }

  &__rating-add {
    display: flex;
    margin-top: 8px;
  }

  /* PDF Upload */
  &__pdf-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;

    @media (min-width: 768px) {
      grid-template-columns: 1fr 1fr;
    }
  }

  &__pdf {
    position: relative;
  }

  &__pdf-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__pdf-area {
    display: block;
    width: 100%;
    border: 2px dashed $p-line-hard;
    border-radius: $p-radius-md;
    background: $p-panel-alt;
    padding: 16px;
    text-align: center;
    cursor: pointer;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &:hover {
      border-color: $p-point-deep;
      background: $p-point-soft;
    }
  }

  &__pdf-area-content {
    display: inline-flex;
    align-items: center;
    gap: 10px;
  }

  &__pdf-ico {
    color: $p-err;
    font-size: 24px;
  }

  &__pdf-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    strong {
      color: $p-ink;
      font-size: 13px;
      font-weight: 800;
    }

    small {
      color: $p-ink-soft;
      font-size: 11px;
    }
  }

  &__pdf-uploading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid $p-warn;
    background: lighten($p-warn, 38%);
    color: darken($p-warn, 20%);
    border-radius: $p-radius-md;
    padding: 10px 12px;
    font-size: 12px;
    font-weight: 700;
  }

  &__pdf-rotate {
    animation: pg-write-spin 1s linear infinite;
    font-size: 16px;
  }

  &__pdf-preview {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid $p-line;
    background: $p-panel;
    border-radius: $p-radius-md;
    padding: 12px;
  }

  &__pdf-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    flex: 1;
  }

  &__pdf-name {
    font-weight: 700;
    color: $p-ink;
    font-size: 12px;
    word-break: break-all;
  }

  &__pdf-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    a {
      color: $p-point-deep;
      text-decoration: none;
      font-size: 12px;
      font-weight: 700;

      &:hover { text-decoration: underline; }
    }
  }
}

@keyframes pg-write-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
